import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  spring,
  interpolate,
  Sequence,
  Video,
  staticFile,
} from "remotion";
import {SPRINGS} from "../presets/editing-styles";
import {CLAMP} from "../presets/idv";

// ─── PALETA EP01 ───────────────────────────────────────────────────────────
const C = {
  bg: "#0A0A0A",
  white: "#FFFFFF",
  magenta: "#FF007F",
  green: "#00CC44",
  red: "#FF3333",
  gold: "#D4AF37",
  muted: "rgba(255,255,255,0.6)",
  card: "#141414",
  cardDim: "#1A1A1A",
  border: "rgba(255,255,255,0.15)",
} as const;

const F = {
  bebas: '"Bebas Neue", "Impact", "Arial Black", sans-serif',
  inter: '"Inter", "SF Pro Display", -apple-system, sans-serif',
};

// ─── UTILS ─────────────────────────────────────────────────────────────────
function spr(frame: number, fps: number, cfg: Record<string, number | boolean> = SPRINGS.snappy, offset = 0) {
  return spring({frame: frame - offset, fps, config: cfg});
}
function lerp(v: number, range: readonly number[], output: readonly number[]) {
  return interpolate(v, range, output, CLAMP);
}

// ─── STAMP DISCLOSURE (overlay permanente) ────────────────────────────────
export const StampDisclosure: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = lerp(frame, [0, 30], [0, 0.55]);
  return (
    <div
      style={{
        position: "absolute",
        bottom: 32,
        left: 32,
        opacity,
        background: "rgba(0,0,0,0.4)",
        borderRadius: 4,
        padding: "4px 8px",
        fontFamily: F.inter,
        fontSize: 20,
        fontWeight: 600,
        letterSpacing: "0.08em",
        color: C.white,
        textTransform: "uppercase",
        pointerEvents: "none",
      }}
    >
      100% EDITADO POR AI
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-01 — Cronômetro 3:00:00 (3s @ 60fps = 180f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR01_Timer: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = 60;
  const s = spr(frame, fps, SPRINGS.snappy);
  const scale = lerp(s, [0, 1], [0.8, 1]);
  const opacity = lerp(s, [0, 1], [0, 1]);
  // blink every 24f
  const blink = Math.floor(frame / 24) % 2 === 0;
  const col = blink ? "#FF3333" : "#FF6666";
  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center"}}>
      <div style={{
        position: "absolute",
        inset: 0,
        background: "radial-gradient(ellipse at center, rgba(255,51,51,0.08) 0%, transparent 70%)",
      }} />
      {/* scanline */}
      <div style={{
        position: "absolute",
        inset: 0,
        backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 4px)",
      }} />
      <div style={{transform: `scale(${scale})`, opacity}}>
        <div style={{
          fontFamily: '"Courier New", Courier, monospace',
          fontSize: 120,
          fontWeight: 700,
          color: col,
          letterSpacing: "0.04em",
          textShadow: `0 0 30px ${col}88`,
          lineHeight: 1,
        }}>
          3:00:00
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-02 — Grid 6 posts com X (2.5s → 150f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR02_Grid: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = 60;
  const cells = [0, 1, 2, 3, 4, 5];
  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center"}}>
      <div style={{display: "grid", gridTemplateColumns: "repeat(3, 160px)", gap: 8}}>
        {cells.map((i) => {
          const delay = i * 5;
          const s = spr(frame, fps, SPRINGS.bouncy, delay);
          const scale = lerp(s, [0, 1], [0, 1]);
          const sat = lerp(frame, [0, 90], [1, 0.3]);
          // X pulse after grid in (after frame 60)
          const xFrame = Math.max(0, frame - 60 - i * 8);
          const xScale = xFrame > 0
            ? 1 + lerp(spring({frame: xFrame, fps, config: SPRINGS.bouncy}), [0, 1], [0, 0.3])
            : 0;
          return (
            <div key={i} style={{
              width: 160, height: 160,
              transform: `scale(${scale})`,
              filter: `saturate(${sat})`,
              position: "relative",
              background: "#2A2A2A",
              borderRadius: 4,
              overflow: "hidden",
            }}>
              {/* thumbnail placeholder */}
              <div style={{
                width: "100%", height: "100%",
                background: `hsl(${i * 30 + 180}, 20%, 25%)`,
              }} />
              {/* X mark */}
              <div style={{
                position: "absolute",
                top: 6, right: 6,
                width: 28, height: 28,
                transform: `scale(${xScale})`,
                color: C.red,
                fontFamily: F.bebas,
                fontSize: 28,
                fontWeight: 900,
                lineHeight: 1,
                opacity: 0.9,
              }}>✕</div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-03 — 3 estilos visuais em sequência (3s → 180f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR03_Styles: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = 60;
  // 3 frames, each ~50f with hard-cut flash between
  const frameIdx = Math.min(2, Math.floor(frame / 55));
  const localFrame = frame % 55;
  // flash: 0→1→0 in 6f
  const flashOp = lerp(localFrame, [0, 3, 6], [1, 1, 0]);

  const frames = [
    {bg: "#87CEEB", label: "PESSOA 1", style: {fontFamily: "Arial, sans-serif", fontSize: 32, color: "#333"}},
    {bg: "#4A90E2", label: "PESSOA 2", style: {fontFamily: '"Roboto", sans-serif', fontSize: 32, color: "#fff"}},
    {bg: "#0A0A0A", label: "PESSOA 3", style: {fontFamily: F.bebas, fontSize: 48, letterSpacing: "0.1em", color: C.white}},
  ];
  const current = frames[frameIdx];

  const slideS = spr(Math.min(localFrame, 12), fps, SPRINGS.snappy);
  const translateX = lerp(slideS, [0, 1], [100, 0]);

  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center"}}>
      <div style={{
        width: 480, height: 480,
        transform: `translateX(${translateX}%)`,
        background: current.bg,
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        padding: 20,
        position: "relative",
        overflow: "hidden",
      }}>
        {/* accent strip for frame 3 */}
        {frameIdx === 2 && (
          <div style={{position: "absolute", top: 0, left: 0, right: 0, height: 4, background: C.magenta}} />
        )}
        <div style={{...current.style, textAlign: "center"}}>{current.label}</div>
      </div>
      {/* flash overlay */}
      <div style={{
        position: "absolute", inset: 0,
        background: "#FFFFFF",
        opacity: localFrame < 6 && frame > 0 ? flashOp : 0,
        pointerEvents: "none",
      }} />
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-04 — Nameplate FLARE hero card (4s → 240f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR04_FlareNameplate: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = 60;

  const cardS = spr(frame, fps, {damping: 15, stiffness: 200});
  const translateY = lerp(cardS, [0, 1], [-120, 0]);
  const opacity = lerp(frame, [0, 20], [0, 1]);

  // underline after card settles (frame 40+)
  const lineS = spr(frame, fps, SPRINGS.snappy, 40);
  const lineScale = lerp(lineS, [0, 1], [0, 1]);

  // glow pulse
  const glowOp = 0.3 + 0.1 * Math.sin((frame / fps) * Math.PI * 2);

  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center"}}>
      <div style={{
        transform: `translateY(${translateY}%)`,
        opacity,
        width: 600,
        padding: "28px 40px",
        background: C.card,
        border: `1px solid ${C.magenta}`,
        borderRadius: 8,
        boxShadow: `0 0 40px rgba(255,0,127,${glowOp})`,
        textAlign: "center",
        position: "relative",
      }}>
        <div style={{
          fontFamily: F.bebas,
          fontSize: 96,
          color: C.white,
          letterSpacing: "0.12em",
          lineHeight: 1,
        }}>
          FLARE
        </div>
        {/* underline */}
        <div style={{
          height: 2,
          background: C.magenta,
          marginTop: 8,
          transformOrigin: "left center",
          transform: `scaleX(${lineScale})`,
        }} />
        <div style={{
          fontFamily: F.inter,
          fontWeight: 600,
          fontSize: 22,
          color: C.magenta,
          letterSpacing: "0.08em",
          marginTop: 12,
        }}>
          CMO AI
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-05 — 3 boxes abaixo do FLARE (4s → 240f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR05_ThreeBoxes: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = 60;

  // FLARE card (same as BR04 but static after entry)
  const cardS = spr(frame, fps, {damping: 15, stiffness: 200});
  const translateY = lerp(cardS, [0, 1], [-60, 0]);
  const cardOpacity = lerp(frame, [0, 10], [0, 1]);
  const lineScale = lerp(spr(frame, fps, SPRINGS.snappy, 30), [0, 1], [0, 1]);

  // connector lines (appear frame 50+)
  const boxes = [
    {label: "COPY", icon: "✏️"},
    {label: "DESIGN", icon: "🎨"},
    {label: "VÍDEO", icon: "▶"},
  ];

  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 0}}>
      {/* FLARE card */}
      <div style={{
        transform: `translateY(${translateY}px)`,
        opacity: cardOpacity,
        width: 500,
        padding: "20px 32px",
        background: C.card,
        border: `1px solid ${C.magenta}`,
        borderRadius: 8,
        textAlign: "center",
        marginBottom: 0,
      }}>
        <div style={{fontFamily: F.bebas, fontSize: 72, color: C.white, letterSpacing: "0.12em", lineHeight: 1}}>FLARE</div>
        <div style={{height: 2, background: C.magenta, marginTop: 6, transformOrigin: "left", transform: `scaleX(${lineScale})`}} />
        <div style={{fontFamily: F.inter, fontWeight: 600, fontSize: 18, color: C.magenta, marginTop: 8}}>CMO AI</div>
      </div>

      {/* connector lines + boxes */}
      <div style={{display: "flex", gap: 16, alignItems: "flex-start", marginTop: 0}}>
        {boxes.map((box, i) => {
          const lineDelay = 50 + i * 10;
          const lineS = spr(frame, fps, SPRINGS.snappy, lineDelay);
          const lineH = lerp(lineS, [0, 1], [0, 40]);
          const boxDelay = 80 + i * 12;
          const boxS = spr(frame, fps, SPRINGS.bouncy, boxDelay);
          const boxScale = lerp(boxS, [0, 1], [0, 1]);
          const dotProgress = frame > lineDelay + 20
            ? lerp(frame - (lineDelay + 20), [0, 18], [0, 1])
            : 0;
          return (
            <div key={i} style={{display: "flex", flexDirection: "column", alignItems: "center", width: 150}}>
              {/* line */}
              <div style={{position: "relative", width: 1, overflow: "hidden", height: 40}}>
                <div style={{
                  position: "absolute",
                  top: 0, left: 0,
                  width: 1, height: lineH,
                  background: C.magenta,
                  opacity: 0.6,
                }} />
                {/* dot traversal */}
                {dotProgress > 0 && (
                  <div style={{
                    position: "absolute",
                    top: dotProgress * lineH - 5,
                    left: -4,
                    width: 9, height: 9,
                    borderRadius: "50%",
                    background: C.gold,
                  }} />
                )}
              </div>
              {/* box */}
              <div style={{
                width: 150, height: 80,
                transform: `scale(${boxScale})`,
                background: C.cardDim,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
              }}>
                <span style={{fontSize: 22}}>{box.icon}</span>
                <span style={{fontFamily: F.inter, fontWeight: 700, fontSize: 14, color: C.white, letterSpacing: "0.06em"}}>{box.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-06 — Icons Time Design (3.5s → 210f)
// ═══════════════════════════════════════════════════════════════════════════
const IconRow: React.FC<{icons: {icon: string; label: string}[]; header: string; badge?: boolean}> = ({icons, header, badge}) => {
  const frame = useCurrentFrame();
  const fps = 60;
  const headerS = spr(frame, fps, SPRINGS.snappy);
  const headerOp = lerp(headerS, [0, 1], [0, 1]);

  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 24}}>
      {/* header */}
      <div style={{
        fontFamily: F.inter,
        fontSize: 18,
        fontWeight: 700,
        letterSpacing: "0.1em",
        color: C.magenta,
        textTransform: "uppercase",
        opacity: headerOp,
      }}>
        {header}
      </div>
      {/* icons row */}
      <div style={{display: "flex", gap: 24}}>
        {icons.map((item, i) => {
          const delay = 18 + i * 7;
          const s = spr(frame, fps, SPRINGS.snappy, delay);
          const tY = lerp(s, [0, 1], [20, 0]);
          const op = lerp(s, [0, 1], [0, 1]);
          const bounceDelay = delay + 20;
          const bounceFrame = Math.max(0, frame - bounceDelay);
          const bs = bounceFrame > 0 ? spring({frame: bounceFrame, fps, config: SPRINGS.bouncy}) : 0;
          const bounce = 1 + lerp(bs, [0, 0.5, 1], [0, 0.08, 0]);

          return (
            <div key={i} style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
              transform: `translateY(${tY}px) scale(${bounce})`,
              opacity: op,
            }}>
              <div style={{
                width: 80, height: 80,
                background: C.cardDim,
                borderRadius: "50%",
                display: "flex", justifyContent: "center", alignItems: "center",
                fontSize: 32,
                position: "relative",
              }}>
                {item.icon}
                {badge && (
                  <div style={{
                    position: "absolute", top: 2, right: 2,
                    width: 16, height: 16,
                    border: `2px solid ${C.magenta}`,
                    borderRadius: "50%",
                    animation: "none",
                    transform: `rotate(${(frame / 60) * 360}deg)`,
                    borderTopColor: "transparent",
                  }} />
                )}
              </div>
              <span style={{fontFamily: F.inter, fontWeight: 600, fontSize: 14, color: C.white, letterSpacing: "0.05em"}}>{item.label}</span>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

export const BR06_TeamDesign: React.FC = () => (
  <IconRow
    header="TIME DESIGN"
    icons={[
      {icon: "🖼", label: "POST"},
      {icon: "🃏", label: "CARROSSEL"},
      {icon: "🖼️", label: "CAPA"},
      {icon: "📢", label: "ANÚNCIO"},
    ]}
  />
);

// ═══════════════════════════════════════════════════════════════════════════
// BR-07 — Icons Time Video (3.5s → 210f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR07_TeamVideo: React.FC = () => (
  <IconRow
    header="TIME VÍDEO"
    badge
    icons={[
      {icon: "📱", label: "REEL"},
      {icon: "⬆", label: "SHORT"},
      {icon: "▶", label: "ANÚNCIO"},
    ]}
  />
);

// ═══════════════════════════════════════════════════════════════════════════
// BR-08 — Split ✓/✗ por time (3s → 180f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR08_CheckCross: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = 60;
  const lineS = spr(frame, fps, SPRINGS.snappy);
  const lineH = lerp(lineS, [0, 1], [0, 1]);

  const leftItems = ["Posts estáticos", "Carrosséis", "Capas", "~~Reels~~", "~~Vídeo ads~~"];
  const rightItems = ["Reels", "YouTube Shorts", "Vídeo ads", "~~Posts estáticos~~", "~~Carrosséis~~"];

  const renderItems = (items: string[], dir: "left" | "right") => items.map((item, i) => {
    const isStrike = item.startsWith("~~");
    const text = isStrike ? item.slice(2, -2) : item;
    const delay = 20 + i * 5;
    const s = spr(frame, fps, SPRINGS.snappy, delay);
    const tX = lerp(s, [0, 1], [dir === "left" ? 10 : -10, 0]);
    const op = lerp(s, [0, 1], [0, 1]);
    const iconDelay = delay + 6;
    const iconS = spr(frame, fps, SPRINGS.bouncy, iconDelay);
    const iconScale = lerp(iconS, [0, 1], [0, 1]);
    return (
      <div key={i} style={{
        display: "flex", alignItems: "center", gap: 8,
        transform: `translateX(${tX}px)`, opacity: op,
        marginBottom: 8,
      }}>
        <span style={{
          transform: `scale(${iconScale})`,
          fontSize: 18,
          color: isStrike ? C.red : C.green,
          minWidth: 20,
          display: "inline-block",
        }}>
          {isStrike ? "✕" : "✓"}
        </span>
        <span style={{
          fontFamily: F.inter, fontWeight: 500, fontSize: 20,
          color: isStrike ? C.muted : C.white,
          textDecoration: isStrike ? "line-through" : "none",
        }}>
          {text}
        </span>
      </div>
    );
  });

  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center"}}>
      <div style={{display: "flex", width: 900, gap: 0, alignItems: "flex-start"}}>
        {/* left col */}
        <div style={{flex: 1, padding: "0 24px"}}>
          <div style={{fontFamily: F.inter, fontWeight: 700, fontSize: 16, letterSpacing: "0.1em", color: C.white, marginBottom: 16, textTransform: "uppercase"}}>DESIGN</div>
          {renderItems(leftItems, "left")}
        </div>
        {/* divider */}
        <div style={{
          width: 1, background: C.magenta, opacity: 0.5,
          transformOrigin: "top",
          transform: `scaleY(${lineH})`,
          alignSelf: "stretch",
          minHeight: 280,
        }} />
        {/* right col */}
        <div style={{flex: 1, padding: "0 24px"}}>
          <div style={{fontFamily: F.inter, fontWeight: 700, fontSize: 16, letterSpacing: "0.1em", color: C.white, marginBottom: 16, textTransform: "uppercase"}}>VÍDEO</div>
          {renderItems(rightItems, "right")}
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-09 — FLARE árbitro → Design/Video (3s → 180f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR09_FlareDivide: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = 60;
  const circS = spr(frame, fps, SPRINGS.bouncy);
  const circScale = lerp(circS, [0, 1], [0, 1]);

  // arrows extend after circle (frame 20+)
  const arrL = spr(frame, fps, SPRINGS.snappy, 20);
  const arrowLeft = lerp(arrL, [0, 1], [0, -130]);
  const arrR = spr(frame, fps, SPRINGS.snappy, 20);
  const arrowRight = lerp(arrR, [0, 1], [0, 130]);

  // dot traversal
  const dotL = frame > 40 ? lerp(frame - 40, [0, 18], [0, 1]) : 0;
  const dotR = frame > 40 ? lerp(frame - 40, [0, 18], [0, 1]) : 0;

  // boxes after dots arrive (frame 60+)
  const boxS = spr(frame, fps, SPRINGS.bouncy, 60);
  const boxScale = lerp(boxS, [0, 1], [0, 1]);

  // center pulse
  const pulseDelay = 90;
  const pulseFrame = Math.max(0, frame - pulseDelay);
  const pulseS = pulseFrame > 0 ? spring({frame: pulseFrame, fps, config: SPRINGS.bouncy}) : 0;
  const pulse = 1 + lerp(pulseS, [0, 0.5, 1], [0, 0.15, 0]);

  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center"}}>
      <div style={{display: "flex", alignItems: "center", width: 700, justifyContent: "center", position: "relative"}}>
        {/* Left box */}
        <div style={{
          width: 150, height: 70,
          transform: `translateX(${arrowLeft + 130}px) scale(${boxScale})`,
          background: C.cardDim,
          border: `1px solid ${C.border}`,
          borderRadius: 6,
          display: "flex", justifyContent: "center", alignItems: "center",
          fontFamily: F.inter, fontWeight: 700, fontSize: 16, color: C.white,
          letterSpacing: "0.08em",
          position: "absolute",
          left: 0,
        }}>DESIGN</div>

        {/* Left arrow dot */}
        {dotL > 0 && (
          <div style={{
            position: "absolute",
            left: 200 + (arrowLeft * dotL),
            width: 10, height: 10,
            borderRadius: "50%",
            background: C.gold,
            transform: "translate(-50%, -50%)",
            top: "50%",
          }} />
        )}

        {/* Center FLARE circle */}
        <div style={{
          width: 90, height: 90,
          borderRadius: "50%",
          background: C.magenta,
          display: "flex", justifyContent: "center", alignItems: "center",
          transform: `scale(${circScale * pulse})`,
          zIndex: 2,
        }}>
          <span style={{fontFamily: F.bebas, fontSize: 16, color: C.white, letterSpacing: "0.1em"}}>FLARE</span>
        </div>

        {/* Right arrow dot */}
        {dotR > 0 && (
          <div style={{
            position: "absolute",
            right: 200 - (arrowRight * dotR) + 130,
            width: 10, height: 10,
            borderRadius: "50%",
            background: C.gold,
            transform: "translate(50%, -50%)",
            top: "50%",
          }} />
        )}

        {/* Right box */}
        <div style={{
          width: 150, height: 70,
          transform: `translateX(${-(arrowRight - 130)}px) scale(${boxScale})`,
          background: C.cardDim,
          border: `1px solid ${C.border}`,
          borderRadius: 6,
          display: "flex", justifyContent: "center", alignItems: "center",
          fontFamily: F.inter, fontWeight: 700, fontSize: 16, color: C.white,
          letterSpacing: "0.08em",
          position: "absolute",
          right: 0,
        }}>VÍDEO</div>
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-10 — Split 3h → 20min (4s → 240f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR10_Split: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = 60;

  const lineS = spr(frame, fps, SPRINGS.snappy);
  const lineH = lerp(lineS, [0, 1], [0, 1]);

  const leftS = spr(frame, fps, SPRINGS.snappy);
  const leftX = lerp(leftS, [0, 1], [-100, 0]);

  const rightS = spr(frame, fps, SPRINGS.snappy);
  const rightX = lerp(rightS, [0, 1], [100, 0]);

  // strikethrough draws at frame 40
  const strikeS = spr(frame, fps, SPRINGS.snappy, 40);
  const strikeW = lerp(strikeS, [0, 1], [0, 1]);

  // green number pulse at frame 60
  const greenPulseFrame = Math.max(0, frame - 60);
  const greenPulseS = greenPulseFrame > 0 ? spring({frame: greenPulseFrame, fps, config: SPRINGS.bouncy}) : 0;
  const greenPulse = 1 + lerp(greenPulseS, [0, 0.5, 1], [0, 0.1, 0]);

  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center"}}>
      <div style={{display: "flex", width: 900, height: 400, position: "relative"}}>
        {/* Left — 3:00:00 */}
        <div style={{
          flex: 1,
          display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
          transform: `translateX(${leftX}%)`,
          background: "radial-gradient(ellipse at center, rgba(255,51,51,0.06) 0%, transparent 70%)",
          gap: 12,
          position: "relative",
        }}>
          <div style={{
            fontFamily: F.bebas, fontSize: 90, color: C.red, lineHeight: 1,
            position: "relative",
          }}>
            3:00:00
            {/* strikethrough */}
            <div style={{
              position: "absolute",
              top: "50%", left: 0,
              width: "100%", height: 4,
              background: C.red,
              transformOrigin: "left",
              transform: `scaleX(${strikeW})`,
            }} />
          </div>
          <div style={{fontFamily: F.inter, fontSize: 18, color: C.muted}}>por post</div>
        </div>

        {/* center divider */}
        <div style={{
          width: 1, background: C.magenta, opacity: 0.5,
          transformOrigin: "top", transform: `scaleY(${lineH})`,
          alignSelf: "stretch",
        }} />

        {/* Right — 0:20:00 */}
        <div style={{
          flex: 1,
          display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
          transform: `translateX(${rightX}%) scale(${greenPulse})`,
          background: "radial-gradient(ellipse at center, rgba(0,204,68,0.06) 0%, transparent 70%)",
          gap: 12,
        }}>
          <div style={{
            fontFamily: F.bebas, fontSize: 90, color: C.green, lineHeight: 1,
            textShadow: `0 0 30px rgba(0,204,68,0.5)`,
          }}>
            0:20:00
          </div>
          <div style={{fontFamily: F.inter, fontSize: 18, color: C.muted}}>por post hoje</div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-11 — Mosaico 9 posts (3s → 180f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR11_Mosaic: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = 60;
  const cells = [0, 1, 2, 3, 4, 5, 6, 7, 8];

  // border appears frame 70
  const borderS = spr(frame, fps, SPRINGS.snappy, 70);
  const borderW = lerp(borderS, [0, 1], [0, 2]);

  // label after border
  const labelOp = lerp(frame, [85, 100], [0, 1]);

  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 12}}>
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3, 110px)", gap: 4,
        outline: `${borderW}px solid ${C.magenta}`,
        borderRadius: 4,
        overflow: "hidden",
      }}>
        {cells.map((i) => {
          const delay = i * 3;
          const s = spr(frame, fps, SPRINGS.bouncy, delay);
          const scale = lerp(s, [0, 1], [0, 1]);
          const op = lerp(s, [0, 1], [0, 1]);
          return (
            <div key={i} style={{
              width: 110, height: 110,
              transform: `scale(${scale})`, opacity: op,
              background: `hsl(${280 + i * 15}, 40%, ${15 + (i % 3) * 5}%)`,
              display: "flex", justifyContent: "center", alignItems: "center",
            }}>
              <div style={{
                width: 40, height: 40,
                borderRadius: 4,
                background: C.magenta,
                opacity: 0.3 + (i % 3) * 0.1,
              }} />
            </div>
          );
        })}
      </div>
      <div style={{
        fontFamily: F.inter, fontWeight: 700, fontSize: 18,
        color: C.magenta, letterSpacing: "0.1em",
        opacity: labelOp,
        textTransform: "uppercase",
      }}>
        a mesma marca
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-12 — Ribbon "100% AI EDITED" glitch (4s → 240f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR12_AIEditedRibbon: React.FC = () => {
  const frame = useCurrentFrame();
  // glitch: frames 0-9
  const isGlitch = frame < 9;
  const glitchX = isGlitch
    ? [8, -5, 0][Math.min(2, Math.floor(frame / 3))]
    : 0;
  const ribbonOp = frame > 0 ? 1 : 0;

  // chromatic aberration during glitch
  const rOffset = isGlitch ? 3 : 0;

  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center"}}>
      <div style={{
        width: "90%",
        height: 60,
        background: C.magenta,
        display: "flex", justifyContent: "center", alignItems: "center",
        transform: `translateX(${glitchX}px)`,
        opacity: ribbonOp,
        position: "relative",
        overflow: "hidden",
      }}>
        {/* scanline */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "repeating-linear-gradient(0deg, rgba(0,0,0,0.08) 0px, rgba(0,0,0,0.08) 1px, transparent 1px, transparent 4px)",
        }} />
        {/* red channel offset (glitch) */}
        {isGlitch && (
          <div style={{
            position: "absolute",
            fontFamily: F.bebas, fontSize: 40, letterSpacing: "0.12em", color: "rgba(255,0,0,0.5)",
            transform: `translateX(${rOffset}px)`,
            userSelect: "none",
          }}>⚡ 100% AI EDITED</div>
        )}
        <div style={{
          fontFamily: F.bebas, fontSize: 40, letterSpacing: "0.12em",
          color: C.white, userSelect: "none", position: "relative",
        }}>
          ⚡ 100% AI EDITED
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-13 — "TIME CRIATIVO" pulsa + seta (4s → 240f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR13_TimeCriativo: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = 60;
  const s = spr(frame, fps, SPRINGS.snappy);
  const op = lerp(s, [0, 1], [0, 1]);

  // pulse: 3x cycle, each 48f
  const cycle = frame % 48;
  const pulseS = spring({frame: cycle, fps, config: SPRINGS.bouncy});
  const pulse = cycle < 24 ? 1 + lerp(pulseS, [0, 1], [0, 0.06]) : 1;

  // arrow bounce
  const arrowY = 6 * Math.sin((frame / fps) * Math.PI * 2 / 0.6);

  // micro label fade in at frame 20
  const labelOp = lerp(frame, [20, 36], [0, 0.6]);

  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 12}}>
      <div style={{opacity: labelOp, fontFamily: F.inter, fontSize: 15, color: C.muted, letterSpacing: "0.05em"}}>
        comenta aqui
      </div>
      <div style={{
        fontFamily: F.bebas, fontSize: 72, color: C.magenta, letterSpacing: "0.08em",
        transform: `scale(${pulse})`, opacity: op,
        lineHeight: 1,
      }}>
        TIME CRIATIVO
      </div>
      <div style={{
        fontSize: 36, color: C.white, opacity: op,
        transform: `translateY(${arrowY}px)`,
      }}>
        ↘
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BR-14 — Card final série EP01→EP02 (6s → 360f)
// ═══════════════════════════════════════════════════════════════════════════
export const BR14_EndCard: React.FC = () => {
  const frame = useCurrentFrame();
  const fps = 60;

  const s = spr(frame, fps, {damping: 15, stiffness: 180});
  const tY = lerp(s, [0, 1], [40, 0]);
  const op = lerp(s, [0, 1], [0, 1]);

  const ribbonS = spr(frame, fps, SPRINGS.snappy);
  const ribbonScale = lerp(ribbonS, [0, 1], [0, 1]);

  // arrow pulse
  const arrowPulse = 1 + 0.2 * Math.abs(Math.sin((frame / fps) * Math.PI / 0.8));

  return (
    <AbsoluteFill style={{background: C.bg, justifyContent: "center", alignItems: "center"}}>
      <div style={{
        width: "80%",
        transform: `translateY(${tY}px)`, opacity: op,
        background: "#111111",
        border: `1px solid rgba(255,0,127,0.3)`,
        borderRadius: 12,
        overflow: "hidden",
        padding: "0 0 28px 0",
      }}>
        {/* top ribbon */}
        <div style={{
          height: 4, background: C.magenta,
          transformOrigin: "left",
          transform: `scaleX(${ribbonScale})`,
        }} />
        <div style={{padding: "20px 28px", display: "flex", flexDirection: "column", gap: 8}}>
          <div style={{fontFamily: F.inter, fontSize: 13, fontWeight: 700, letterSpacing: "0.15em", color: C.magenta, textTransform: "uppercase"}}>
            MODO AUTOMÁTICO
          </div>
          <div style={{fontFamily: F.bebas, fontSize: 16, color: "rgba(255,255,255,0.5)", letterSpacing: "0.08em"}}>
            EP 01 / 09
          </div>
          <div style={{height: 1, background: "rgba(255,255,255,0.08)", margin: "4px 0"}} />
          <div style={{fontFamily: F.inter, fontSize: 12, color: "rgba(255,255,255,0.5)", letterSpacing: "0.1em", textTransform: "uppercase"}}>
            PRÓXIMO:
          </div>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <div>
              <div style={{fontFamily: F.bebas, fontSize: 34, color: C.white, letterSpacing: "0.06em", lineHeight: 1}}>
                EP 02 — O CMO
              </div>
              <div style={{fontFamily: F.inter, fontSize: 15, color: "rgba(255,255,255,0.6)", marginTop: 4}}>
                Como o FLARE trabalha
              </div>
            </div>
            <div style={{
              fontSize: 20, color: C.magenta,
              transform: `scale(${arrowPulse})`,
            }}>→</div>
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MODO AUTOMÁTICO EP01 — ASSEMBLY
// Aceita arollPath (relativo ao staticFile) ou mostra placeholder
// ═══════════════════════════════════════════════════════════════════════════
export interface EP01Props {
  arollPath?: string; // ex: "aroll/ep01_aroll.mp4"
}

export const EP01_TOTAL_FRAMES = 4920; // 86s @ 60fps

// B-roll insert definition: {startFrame, durationFrames, component}
type BRollEntry = {
  start: number;
  dur: number;
  Component: React.FC;
};

const BROLLS: BRollEntry[] = [
  {start: 420,  dur: 180, Component: BR01_Timer},         // BR-01: 0:07
  {start: 780,  dur: 150, Component: BR02_Grid},          // BR-02: 0:13
  {start: 1020, dur: 180, Component: BR03_Styles},        // BR-03: 0:17
  {start: 1560, dur: 240, Component: BR04_FlareNameplate},// BR-04: 0:26
  {start: 1860, dur: 240, Component: BR05_ThreeBoxes},    // BR-05: 0:31
  {start: 2220, dur: 210, Component: BR06_TeamDesign},    // BR-06: 0:37
  {start: 2640, dur: 210, Component: BR07_TeamVideo},     // BR-07: 0:44
  {start: 3000, dur: 240, Component: BR08_CheckCross},    // BR-08: 0:50
  {start: 3360, dur: 240, Component: BR09_FlareDivide},   // BR-09: 0:56
  {start: 3660, dur: 240, Component: BR10_Split},         // BR-10: 1:01
  {start: 3900, dur: 180, Component: BR11_Mosaic},        // BR-11: 1:05
  {start: 4080, dur: 240, Component: BR12_AIEditedRibbon},// BR-12: 1:08
  {start: 4440, dur: 300, Component: BR13_TimeCriativo},  // BR-13: 1:14
  {start: 4800, dur: 360, Component: BR14_EndCard},       // BR-14: 1:20
];

export const ModoAutomaticoEP01: React.FC<EP01Props> = ({arollPath}) => {
  return (
    <AbsoluteFill style={{background: "#000", width: 1080, height: 1920}}>
      {/* A-roll layer */}
      {arollPath ? (
        <Video
          src={staticFile(arollPath)}
          style={{width: "100%", height: "100%", objectFit: "cover"}}
        />
      ) : (
        /* Placeholder when no A-roll */
        <AbsoluteFill style={{
          background: "linear-gradient(180deg, #0A0A0A 0%, #111 100%)",
          justifyContent: "center", alignItems: "center",
          flexDirection: "column", gap: 16,
        }}>
          <div style={{fontFamily: F.bebas, fontSize: 48, color: "rgba(255,255,255,0.15)", letterSpacing: "0.1em"}}>
            A-ROLL AQUI
          </div>
          <div style={{fontFamily: F.inter, fontSize: 18, color: "rgba(255,255,255,0.1)"}}>
            Adicione arollPath para montar o vídeo final
          </div>
        </AbsoluteFill>
      )}

      {/* B-roll inserts */}
      {BROLLS.map(({start, dur, Component}, i) => (
        <Sequence key={i} from={start} durationInFrames={dur}>
          <Component />
        </Sequence>
      ))}

      {/* Stamp disclosure — permanente */}
      <StampDisclosure />
    </AbsoluteFill>
  );
};

// ─── EXPORTS para Root.tsx (standalone previews) ───────────────────────────
export {
  BR01_Timer as ModoAutoEP01_BR01,
  BR02_Grid as ModoAutoEP01_BR02,
  BR03_Styles as ModoAutoEP01_BR03,
  BR04_FlareNameplate as ModoAutoEP01_BR04,
  BR05_ThreeBoxes as ModoAutoEP01_BR05,
  BR06_TeamDesign as ModoAutoEP01_BR06,
  BR07_TeamVideo as ModoAutoEP01_BR07,
  BR08_CheckCross as ModoAutoEP01_BR08,
  BR09_FlareDivide as ModoAutoEP01_BR09,
  BR10_Split as ModoAutoEP01_BR10,
  BR11_Mosaic as ModoAutoEP01_BR11,
  BR12_AIEditedRibbon as ModoAutoEP01_BR12,
  BR13_TimeCriativo as ModoAutoEP01_BR13,
  BR14_EndCard as ModoAutoEP01_BR14,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
